import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, useMediaQuery } from '@mui/material';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import MUIDataTable from 'mui-datatables';

import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { trackEvent } from '@intercom/messenger-js-sdk';

import { SearchRounded } from '@mui/icons-material';

import {
  createSessionObject,
  sessionActions,
} from '../../store/actions/session.actions';
import { productActions } from '../../store/actions/product.actions';

import DeleteIcon from '@/shared/assets/icons/delete.svg?react';
import EditIcon from '@/shared/assets/icons/edit.svg?react';

import useDebounce from '../../hooks/useDebounce';
import { useTableFilterConfig } from '../../hooks/useTableFilterConfig';
import { useInitTableSearchParams } from '../../hooks/useInitTableSearchParams';
import { useTableFiltersSearchParams } from '../../hooks/useTableFiltersSearchParams';
import { useInitProductFilterBasedOnTypeParam } from '../../hooks/useInitProductFilterBasedOnTypeParam';
import { useIntercomComponentOnOpenEventTrack } from '../../hooks/useIntercomComponentOnOpenEventTrack';

import { sessionService } from '../../services/sessions';

import getMuiTheme from '../../utils/MUI-Style';
import tableConfig from '../../utils/MUI-table-config';

import {
  classNames,
  encodeForCSV,
  getResponsiveDimension,
  getTransformedLogicTableFilterValue,
  mapOrderToSort,
} from '../../utils';
import { showToast } from '../../utils/toast';
import { DEFAULT_FILTER_KEYS } from '../../utils/config';
import TableHeader from '../UI/TableHeader/TableHeader';
import muiComponents from '../../utils/MUI-table-components';
import { muiTableLoadingSkeleton } from '../../utils/MUI-table-loading-skaleton';

// eslint-disable-next-line import/no-named-as-default-member
import LineGraphReverse from '../LineGraphReverse';
import CustomDateSortHeader from './CustomDateSortHeader';
import { ModalUI } from '../UI/Modal/Modal';
import { ButtonUi } from '../UI/Button/Button';
import { LineChart } from '../UI/LineChart/LineChart';
import { deleteConfirmationStyle } from '../UI/SimpleModalContent/style';
import SimpleModalContent from '../UI/SimpleModalContent/SimpleModalContent';
import { ReassignClientModal } from './ReassignClientModal/ReassignClientModal';
import { ProductDropdown } from '../UI/MuiDatatable/ProductDropdown/ProductDropdown';
import { CustomMuiDataTableFilter } from '../UI/MuiDatatable/CustomMuiDataTableFilter/CustomMuiDataTableFilter';
import { CustomMuiDataTableComplete } from '../UI/MuiDatatable/CustomMuiDataTableComplete/CustomMuiDataTableComplete';
import { CustomMuiDataTableBodyButton } from '../UI/MuiDatatable/CustomMuiDataTableBodyButton/CustomMuiDataTableBodyButton';

const SessionsTable = ({
  authentication,
  label: propLabel,
  products: productsReducer,
  getAllSessions,
  findSessionById,
  getAllProductConfigurations,
  clearSelectedSession,
  clearSessionsData,
  deleteSessionById,
  sessions,
  history,
}) => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const paramPage = Number(params.get('page'));
  const paramLimit = Number(params.get('limit')) || 10;
  const paramSearch = params.get('searchQuery') || '';
  const paramOrder = params.get('order');
  const paramSortType = paramOrder?.split(' ')?.[0];
  const paramDirection = paramOrder?.split(' ')?.[1];
  const paramType = params.get('type');
  const paramCompleted = params.get('completed');
  const paramDecrease = params.get('decrease')?.split(',')?.map(Number);
  const paramStartDate = params
    .get('startDate')
    ?.split(',')
    ?.map((date) => date);

  const isDesktop = useMediaQuery('(min-width: 1025px)');
  const isLaptop = useMediaQuery('(max-width: 1024px) and (min-width: 768px)');

  const [page, setPage] = useState(paramPage / paramLimit || 0);
  const [limit, setLimit] = useState(paramLimit);
  const [currentSort, setCurrentSort] = useState(
    mapOrderToSort(paramDirection) || 'up',
  );
  const [currentSortType, setCurrentSortType] = useState(
    paramSortType || 'start_date',
  );
  const [searchText, setSearchText] = useState(paramSearch);
  const [debouncedSearchTerm, setDebouncedValue] = useDebounce(searchText, 500);

  const [filterOpen, setFilterOpen] = useState(false);

  const [sessionsFilters, setSessionsFilters] = useState({
    ...(!!paramType && {
      type: {
        value: paramType,
      },
    }),
    ...(!!paramCompleted && {
      completed: {
        value: paramCompleted,
      },
    }),
    ...(!!paramDecrease && {
      decrease: {
        value: paramDecrease,
      },
    }),
    ...(!!paramStartDate && {
      start_date: {
        value: paramStartDate,
      },
    }),
  });

  const [selectedProductId, setSelectedProductId] = useState();
  const tableRef = useRef();

  const [reassignModalIsOpen, setReassignModalIsOpen] = useState(false);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [sessionToReassignClient, setSessionToReassignClient] = useState(null);

  const [autoCorrectPage, setAutoCorrectPage] = useState(false);

  const { products } = productsReducer;
  const { data, totalSessions, isLoading } = sessions;
  const { userIsAdmin, practitioner } = authentication;

  const { deleteConfirmation } = deleteConfirmationStyle;

  /**
   * Sets table graph height
   */
  const graphHeight = useMemo(() => {
    if (isDesktop) return getResponsiveDimension(38);
    else if (isLaptop) return getResponsiveDimension(37, 'laptop');
    else return getResponsiveDimension(50, 'mobile');
  }, [isDesktop, isLaptop]);

  /**
   * Sets table graph width
   */
  const graphWidth = useMemo(() => {
    if (isDesktop) return getResponsiveDimension(150);
    else if (isLaptop) return getResponsiveDimension(130, 'laptop');
    else return getResponsiveDimension(250, 'mobile');
  }, [isDesktop, isLaptop]);

  /**
   * Opens the filter's modal and grabs data for the filters
   */
  const onOpenFilterModal = useCallback(() => {
    if (products.length <= 0) getAllProductConfigurations();
    setFilterOpen(true);
  }, [getAllProductConfigurations, products]);

  /**
   * Generates filters
   */
  const sessionsFilter = useMemo(() => {
    let order = '';
    let direction = '';
    let searchQuery = '';
    let isAnonymous = false;
    const type = sessionsFilters?.type?.value?.toLowerCase() || '';
    const completed = sessionsFilters?.completed?.value || null;
    const decrease = sessionsFilters?.decrease?.value;
    const startDate = sessionsFilters?.start_date?.value;

    direction = mapOrderToSort(currentSort);

    if (currentSort && currentSortType && direction !== 'default')
      order = `${currentSortType} ${direction}`;

    if (debouncedSearchTerm) {
      searchQuery = `${debouncedSearchTerm?.toLowerCase()}`;
      if (t('treatments.anon-client-name').toLowerCase().includes(searchQuery))
        isAnonymous = true;
      trackEvent('table-search', {
        table_name: 'Sessions',
      });
    }
    return {
      page: page * limit || 0,
      limit,
      order,
      searchQuery,
      isAnonymous,
      type,
      completed,
      decrease,
      startDate,
    };
  }, [
    t,
    page,
    limit,
    currentSort,
    currentSortType,
    debouncedSearchTerm,
    sessionsFilters,
  ]);

  /**
   * Gets all sessions on filters changes
   */
  useEffect(() => {
    const {
      page,
      limit,
      order,
      searchQuery,
      isAnonymous,
      type,
      completed,
      decrease,
      startDate,
    } = sessionsFilter;
    getAllSessions(
      limit,
      page,
      order,
      searchQuery,
      isAnonymous,
      type,
      completed,
      decrease,
      startDate,
    );
    if (type) {
      const filterProduct = products.find(
        (item) => item.name.toLowerCase() === type,
      );
      setSelectedProductId(filterProduct?.id);
    } else setSelectedProductId(null);
  }, [getAllSessions, sessionsFilter]);

  /**
   * On change product
   * @param product selected product
   */
  const onChangeProduct = useCallback(
    (product) => {
      const { type, ...restFilters } = sessionsFilters || {};
      if (!product || !product.value) setSessionsFilters(restFilters);
      else {
        setSessionsFilters((filters) => ({
          ...filters,
          type: { value: product?.text },
        }));
      }
    },
    [sessionsFilters],
  );

  /**
   * Refresh sessions data
   */
  const refreshCurrentData = useCallback(() => {
    const {
      page,
      limit,
      order,
      searchQuery,
      isAnonymous,
      type,
      completed,
      decrease,
      startDate,
    } = sessionsFilter;
    getAllSessions(
      limit,
      page,
      order,
      searchQuery,
      isAnonymous,
      type,
      completed,
      decrease,
      startDate,
    );
  }, [getAllSessions, sessionsFilter]);

  const openReassignClientModal = useCallback((event, id) => {
    event.stopPropagation();
    findSessionById(id);
    setReassignModalIsOpen(true);
  }, []);

  /**
   * Open delete modal
   */
  const onOpenDeleteModal = useCallback((event, id) => {
    event.stopPropagation();
    setSessionToDelete(id);
    setDeleteModalIsOpen(true);
  }, []);

  /**
   * Close delete modal
   */
  const onCloseDeleteModal = useCallback(() => setDeleteModalIsOpen(false), []);

  const closeReassignClientModal = useCallback(() => {
    setReassignModalIsOpen(false);
  }, []);

  /**
   * Delete session handler
   */
  const onDeleteSession = useCallback(async () => {
    await deleteSessionById(sessionToDelete);
    refreshCurrentData();
    setDeleteModalIsOpen(false);
  }, [deleteSessionById, sessionToDelete, refreshCurrentData]);

  /**
   * Sort handler
   * @param type sort property
   */
  const handleSort = useCallback(
    (type) => {
      let nextSort;
      if (currentSort === 'down') nextSort = 'up';
      else if (currentSort === 'up') nextSort = 'default';
      else if (currentSort === 'default') nextSort = 'down';

      setCurrentSortType(type);
      setCurrentSort(nextSort);
    },
    [currentSort],
  );

  /**
   * Generates export CSV data
   */
  const generateExportData = (data) =>
    data.map((item) => createSessionObject(item, true));

  const getExportedData = useCallback(async () => {
    const {
      order,
      searchQuery,
      isAnonymous,
      type,
      completed,
      decrease,
      startDate,
    } = sessionsFilter;
    try {
      const dataToExport = await sessionService.getAllSessions(
        totalSessions,
        0,
        order,
        searchQuery,
        isAnonymous,
        false,
        type,
        completed,
        decrease,
        startDate,
      );
      return generateExportData(dataToExport?.data?.sessions);
    } catch (error) {
      showToast(t('errors.get.sessions'), 'error', 3000);
    }
  }, [totalSessions, sessionsFilter, t]);

  let attributes = [];

  const onOpenDetails = useCallback(
    (rowData) => {
      const id = rowData[0]?.props?.children;
      const clientName = rowData[2]?.props?.children?.props?.clientName;

      history.push({
        pathname: `/admin/sessions/${id}`,
        state: { sessionClientName: clientName },
      });
    },
    [history],
  );

  /**
   * Init table's options
   */
  const options = useCallback(
    () => ({
      serverSide: true,
      page,
      rowsPerPage: limit,
      count: totalSessions,
      sort: true,
      onChangePage: (currentPage) => {
        setPage(currentPage);
      },
      onChangeRowsPerPage: (numberOfRows) => {
        const { page: offset, limit } = sessionsFilter;
        const showedRows = offset + limit;
        const pagesLimit = Math.ceil(totalSessions / numberOfRows);
        if (showedRows <= numberOfRows && page !== 0) {
          const newPage = Math.floor(numberOfRows / showedRows) - 1;
          if (newPage >= pagesLimit) setPage(pagesLimit - 1);
          else setPage(newPage);
        } else if (showedRows > numberOfRows) {
          const newPage = Math.floor(showedRows / numberOfRows);
          if (newPage >= pagesLimit) setPage(pagesLimit - 1);
          else setPage(newPage);
        }
        setLimit(numberOfRows);
      },
      onSearchChange: (text) => setSearchText(text || ''),
      onDownload: (buildHead, buildBody, columns) => {
        (async () => {
          const downloadColumns = attributes
            .map((col) => {
              const existingCol = columns.find(
                ({ name }) => name === col?.name,
              );
              if (!existingCol && col?.options?.download)
                return { name: col.name, download: true };

              return existingCol;
            })
            .filter((col) => col);
          try {
            const exportTableData = await getExportedData();
            const bodyRows = exportTableData.map((row) => {
              const rowData = [];
              downloadColumns?.forEach((column) => {
                if (column?.name === t('forms.notes'))
                  rowData.push(encodeForCSV(row?.[column?.name]?.[0]) || '');
                else rowData.push(row?.[column?.name] || '');
              });
              return { data: rowData };
            });
            const csv = `\uFEFF${buildHead(columns)}${buildBody(bodyRows)}`;

            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = 'export-table-data.csv';
            document.body.appendChild(link);
            link.click();
            link.remove();
          } catch (err) {
            console.error('Export failed:', err);
          }
        })();

        trackEvent('export-table-data', {
          table_name: 'Sessions',
        });

        return false;
      },
      downloadOptions: {
        separator: ';',
        filterOptions: {
          useDisplayedColumnsOnly: false,
          useDisplayedRowsOnly: false,
        },
      },
      searchText,
      searchAlwaysOpen: !userIsAdmin,
    }),
    [
      page,
      limit,
      totalSessions,
      searchText,
      userIsAdmin,
      history,
      sessionsFilter,
      attributes,
      t,
      getExportedData,
    ],
  );

  /**
   * Generates table rows
   */
  const dataToShow = (data || []).map((item) => createSessionObject(item));

  /**
   * Render talbe haed
   */
  const renderHead = useCallback(
    (mappedSortTypes, key) => {
      const sortType = mappedSortTypes[key];
      return (
        <CustomDateSortHeader
          key={`sort-header-${key}`}
          currentSort={currentSortType === sortType ? currentSort : 'default'}
          label={key}
          type={sortType}
          handleSort={handleSort}
        />
      );
    },
    [currentSort, currentSortType, handleSort],
  );

  /**
   * Render table actions
   */
  const renderActions = useCallback(
    (value, tableMeta, updateValue) => {
      const [idWrapper] = tableMeta.rowData;
      const id = idWrapper?.props?.children;

      return (
        <CustomMuiDataTableBodyButton
          isPrimary={!!practitioner}
          isSecondary={!!practitioner}
          firstButtonIcon={<EditIcon />}
          secondButtonIcon={<DeleteIcon />}
          onPrimaryActionButton={(e) => openReassignClientModal(e, id)}
          onSecondaryActionButton={(e) => {
            onOpenDeleteModal(e, id);
          }}
          additionalButtons={[
            <ButtonUi
              key='client-details'
              isIconBtn
              className='table-action-btn primary details-btn'
              size='small'
              onClickHandler={() => onOpenDetails(tableMeta.rowData)}
            >
              <SearchRounded />
            </ButtonUi>,
          ]}
        />
      );
    },
    [onOpenDeleteModal, onOpenDetails, openReassignClientModal, practitioner],
  );

  /*
   * Render session's complete status
   */
  const renderCompleteStatus = useCallback(
    (status) => <CustomMuiDataTableComplete result={status} />,
    [],
  );

  /**
   * Render session's SUD score
   */
  const renderSUDScore = useCallback(
    (value) =>
      value.length > 0 ? (
        <LineChart
          small
          values={value}
          height={graphHeight}
          width={graphWidth}
        />
      ) : (
        '-'
      ),
    [graphHeight, graphWidth],
  );

  /**
   * Render session's VoC score
   */
  const renderVoCScore = useCallback(
    (value) =>
      value.length > 0 ? (
        <LineGraphReverse
          small
          values={value}
          height={graphHeight}
          width={graphWidth}
        />
      ) : (
        '-'
      ),
    [graphHeight, graphWidth],
  );

  /**
   * Generates table attributes
   */
  const mappedSortTypes = {
    [t('forms.client_name')]: 'client_name',
    [t('forms.type')]: 'type',
    [t('forms.duration')]: 'duration',
    [t('forms.sud_scores')]: 'decrease',
    [t('forms.validity_of_cognition_array')]: 'voc_decrease',
    [t('forms.start_date')]: 'start_date',
    [t('forms.completed')]: 'completed',
  };
  if (dataToShow && dataToShow.length > 0) {
    const keysToHide = ['id'];
    const excludedKeys = [t('forms.gen_id')];
    const excludedkeysToDownload = [t('forms.notes')];
    attributes = Object.keys(dataToShow[0]).map((key) => {
      if (keysToHide.includes(key))
        return {
          name: key,
          options: {
            display: false,
            sort: false,
          },
        };

      if (excludedKeys.includes(key))
        return {
          name: key,
          options: {
            display: 'excluded',
            sort: false,
            download: false,
          },
        };

      if (excludedkeysToDownload.includes(key))
        return {
          name: key,
          options: {
            display: false,
            sort: false,
            download: true,
            print: true,
            viewColumns: false,
          },
        };

      if (key === t('forms.sud_scores')) {
        return {
          name: key,
          options: {
            display: true,
            filter: false,
            sort: false,
            customHeadRender: () => renderHead(mappedSortTypes, key),
            customBodyRender: (value) => renderSUDScore(value),
          },
        };
      }
      if (key === t('forms.validity_of_cognition_array')) {
        return {
          name: key,
          options: {
            display: true,
            filter: false,
            sort: false,
            customBodyRender: (value) => renderVoCScore(value),
            customHeadRender: () => renderHead(mappedSortTypes, key),
          },
        };
      }
      if (key === t('forms.completed')) {
        return {
          name: key,
          options: {
            display: true,
            filter: false,
            sort: false,
            customHeadRender: () => renderHead(mappedSortTypes, key),
            customBodyRender: (value, tableMeta) =>
              renderCompleteStatus(tableMeta.rowData[8]),
          },
        };
      }
      if (mappedSortTypes[key]) {
        return {
          name: key,
          options: {
            customHeadRender: () => renderHead(mappedSortTypes, key),
          },
        };
      }
      return {
        name: key,
        options: { sort: false },
      };
    });

    attributes.push({
      name: '',
      options: {
        filter: false,
        sort: false,
        empty: true,
        setCellProps: (value) => ({
          className: 'action',
        }),
        customBodyRender: (value, tableMeta, updateValue) =>
          renderActions(value, tableMeta, updateValue),
      },
    });
  }

  /**
   * Generates the table filter's config
   */
  const filtersConfig = useTableFilterConfig('sessions');

  /**
   * Closes the filter's modal and save data for the filters
   */
  const onSaveFilters = useCallback(
    (filters) => setSessionsFilters(filters),
    [],
  );

  /**
   * Checks does table have data
   */
  const isDataTableEmpty = dataToShow.length <= 0;

  /**
   * Flag to checks does filters active
   */
  const isFilters =
    Object.keys(
      Object.entries(sessionsFilters || {}).filter(
        ([k]) => !DEFAULT_FILTER_KEYS.includes(k),
      ),
    )?.length > 0;

  /**
   * mui datatable optional class names
   */
  const muiTableMods = {
    loading: isLoading,
    'no-data': isDataTableEmpty,
    'search-open': !userIsAdmin,
    filters: isFilters,
  };

  /**
   * Sets table current active filters based on the table search params
   */
  useTableFiltersSearchParams(
    [
      page,
      setPage,
      limit,
      setLimit,
      currentSort,
      setCurrentSort,
      currentSortType,
      setCurrentSortType,
      'start_date',
      setSearchText,
      debouncedSearchTerm,
      setDebouncedValue,
    ],
    [sessionsFilters, setSessionsFilters],
    false,
    [selectedProductId, setSelectedProductId],
  );

  /**
   * Sets table search params based on the current active filters
   */
  useInitTableSearchParams(
    sessionsFilter,
    [autoCorrectPage, setAutoCorrectPage],
    [],
  );

  /**
   * Reset page for search if it's not the first page and there is no results
   */
  useEffect(() => {
    const pagesLimit = Math.ceil(totalSessions / limit);
    if (page > pagesLimit && debouncedSearchTerm) {
      setAutoCorrectPage(true);
      setPage(0);
    }
  }, [debouncedSearchTerm, limit, page, totalSessions]);

  /**
   * Reset page for existings filters if it's not the first page and there is no results
   */
  useEffect(() => {
    const definedLoading = isLoading !== undefined && !isLoading;
    if (isFilters && dataToShow.length <= 0 && definedLoading && page > 0) {
      setAutoCorrectPage(true);
      setPage(0);
    }
  }, [dataToShow, isFilters, page, isLoading]);

  /**
   * Sets table product filter based on the table product type search param
   */
  useInitProductFilterBasedOnTypeParam(
    [selectedProductId, setSelectedProductId],
    paramType,
  );

  useIntercomComponentOnOpenEventTrack('page-visit', {
    name: 'Sessions',
  });

  /**
   * Clears sessions on leaving the page
   */
  useEffect(
    () => () => {
      clearSessionsData();
    },
    [],
  );

  return (
    <>
      <TableHeader
        isButton={false}
        title='sessions.title'
        subTitle='sessions.subtitle'
      />
      <Box className='table-container'>
        <StyledEngineProvider injectFirst>
          <ThemeProvider
            theme={getMuiTheme({
              '&:nth-of-type(8)': {
                width: 100,
              },
            })}
          >
            <MUIDataTable
              ref={tableRef}
              data={isLoading ? muiTableLoadingSkeleton('body') : dataToShow}
              columns={
                isLoading ? muiTableLoadingSkeleton('header') : attributes
              }
              options={tableConfig(
                options(),
                isLoading,
                isDataTableEmpty,
                tableRef,
                isFilters,
                onOpenFilterModal,
                getExportedData,
                'Sessions',
                <ProductDropdown
                  className='toolbar-btn'
                  handleChange={onChangeProduct}
                  selectedId={selectedProductId}
                  disabled={isLoading}
                />,
              )}
              components={muiComponents(sessionsFilters, setSessionsFilters)}
              className={classNames('mui-data-table sessions', muiTableMods)}
              title=''
            />
          </ThemeProvider>
        </StyledEngineProvider>
      </Box>
      {/* Delete confirmation modal */}
      <ModalUI
        modalSx={deleteConfirmation}
        open={deleteModalIsOpen}
        isCloseBtn={false}
        onCloseHandler={onCloseDeleteModal}
        className='confirmation-modal'
        modalTitle={t('alerts.titles.delete')}
      >
        <SimpleModalContent
          content={['alerts.confirm.session']}
          mainBtn='buttons.delete'
          onMainBtnClick={onDeleteSession}
          secondaryBtn='buttons.cancel'
          onSecondaryBtnClick={onCloseDeleteModal}
        />
      </ModalUI>
      {/* Reassign session's client modal */}
      {reassignModalIsOpen && (
        <ReassignClientModal
          reassignModalIsOpen={reassignModalIsOpen}
          closeReassignClientModal={closeReassignClientModal}
          refreshData={refreshCurrentData}
        />
      )}
      {/* Filter modal */}
      {filterOpen && (
        <CustomMuiDataTableFilter
          isOpen={filterOpen}
          setFilterState={setFilterOpen}
          config={filtersConfig}
          filters={sessionsFilters}
          onSaveHandler={onSaveFilters}
          tableName='Sessions'
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  ...state,
});

const mapDispatchToProps = {
  getAllSessions: sessionActions.getAllSessions,
  getSessionCount: sessionActions.getSessionsCount,
  clearSelectedSession: sessionActions.clearSelectedSession,
  clearSessionsData: sessionActions.clearSessionsData,
  getAllProductConfigurations: productActions.getAllProductConfigurations,
  findSessionById: sessionActions.findSessionById,
  deleteSessionById: sessionActions.deleteSessionById,
};

export default connect(mapStateToProps, mapDispatchToProps)(SessionsTable);
